import { useState, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { format } from "date-fns";
import { MoveUpRight, MoveDownRight } from "lucide-react";

import { LineChart, Filter, ChartTooltip } from "components/Chart";
import { TooltipColorDot } from "components/Chart/ChartTooltip.styles";
import { LegendContent } from "components/Chart/Legend";
import { getGainLossDisplay } from "features/Strategy/utils";
import { NoDataMessage } from "features/Strategy/v2/Detail/StrategyDetails.styles";
import { numberSeparator } from "utils";
import { calculateYAxisDomain } from "utils/charts";
import { TIMEFRAME_LABELS } from "utils/constants";
import { DATE_FORMAT } from "utils/date";

import {
  HistoricalPerformanceChartContainer,
  HeaderComponent,
} from "./HistoricalPerformanceChart.styles";
import { HistoricalPerformanceChartPlaceholder } from "./HistoricalPerformanceChartPlaceholder";
import { LegendContent as HpLegendContent } from "./Legend";

// Fallback data keys used when callers don't provide explicit line config
const STRATEGY_KEY = "Strategy Value";
const BENCHMARK_KEY = "Benchmark Value (SPY)";

const toFiniteNumber = (val) =>
  typeof val === "number" && isFinite(val) ? val : 0;

export const HistoricalPerformanceChartV2 = (props) => {
  const {
    height = "350px",
    lines,
    timeframe,
    data = [],
    nodata = "No Broker Account Connected or Created",
    isLoading,
    handleFilterClick,
    activePeriod,
    legendContent,
    showLegend = true,
    alwaysShowHeaderBalance = true,
    periodReturns,
  } = props;

  // User interaction state — only these need useState
  const [isTooltipActive, setIsTooltipActive] = useState(false);
  const [hoverPayload, setHoverPayload] = useState(null);

  const dataLen = data.length;
  // Resolve the data key for strategy line — falls back to module-level constant
  const stratKey = (lines && lines[0] && lines[0].key) || STRATEGY_KEY;
  const bmKey = BENCHMARK_KEY;

  // Derive all display values from data + hover state in one memoized pass
  const {
    activeValue,
    gain,
    gainPercentage,
    benchmark,
    benchmarkPerc,
    firstValue,
    formattedGain,
    formattedGainPerc,
    isPositive,
    displayLabel,
  } = useMemo(() => {
    // Default label used whenever no date is being hovered
    const periodLabel =
      TIMEFRAME_LABELS[activePeriod?.toLowerCase()] || "selected period";

    // Empty state — return neutral defaults so the header never shows NaN
    if (!dataLen) {
      return {
        activeValue: 0,
        gain: 0,
        gainPercentage: 0,
        benchmark: null,
        benchmarkPerc: 0,
        firstValue: 0,
        formattedGain: "0.00",
        formattedGainPerc: "0.00",
        isPositive: true,
        displayLabel: periodLabel,
      };
    }

    // Baseline values — first point in the dataset (used to calculate gain)
    const first = toFiniteNumber(data[0][stratKey]);
    const firstBM = toFiniteNumber(data[0][bmKey]);

    // Active point — hover wins, else fall back to the most recent data point
    const last = data[dataLen - 1];
    const point = hoverPayload || {
      stratVal: toFiniteNumber(last[stratKey]),
      bmVal: toFiniteNumber(last[bmKey]),
      bmColor: null,
      timestamp: null,
    };

    // Server-computed period return wins when not hovering — chart series is
    // normalized to initial capital, so client-side math drifts from the truth.
    const serverReturn = !hoverPayload ? periodReturns?.[activePeriod] : null;

    // Strategy gain vs baseline — server return overrides client math when present
    const strat = getGainLossDisplay(first, point.stratVal, serverReturn);

    // Benchmark gain — only computed when a benchmark value exists
    let bm = null;
    let bmPerc = 0;
    if (point.bmVal) {
      const bmDisplay = getGainLossDisplay(first, point.bmVal);
      bm = {
        value: point.bmVal,
        color: point.bmColor,
        gain: bmDisplay.gain,
        percentage: bmDisplay.gainPercentage,
      };
      bmPerc = getGainLossDisplay(firstBM, point.bmVal).gainPercentage;
    }

    // Hover date → shown as "on Mar 15, 2026" label; null when not hovering
    const date = point.timestamp
      ? format(new Date(point.timestamp * 1000), DATE_FORMAT)
      : null;

    return {
      activeValue: point.stratVal,
      gain: strat.gain,
      gainPercentage: strat.gainPercentage,
      benchmark: bm,
      benchmarkPerc: bmPerc,
      firstValue: first,
      formattedGain: strat.formattedGain,
      formattedGainPerc: strat.formattedGainPercentage,
      isPositive: strat.isGain,
      displayLabel: date ? `on ${date}` : periodLabel,
    };
  }, [
    dataLen,
    data,
    stratKey,
    bmKey,
    hoverPayload,
    activePeriod,
    periodReturns,
  ]);

  // Capture the hovered data point — useMemo handles the rest of the math
  const onMouseMove = (e) => {
    if (!e?.activePayload?.length) return;
    const stratVal = Number(e.activePayload[0].value) || 0;
    const bmVal = Number(e.activePayload[1]?.value) || 0;
    setHoverPayload({
      stratVal,
      bmVal,
      bmColor: e.activePayload[1]?.color || null,
      timestamp: e.activePayload[0].payload.name,
    });
  };

  const Icon = isPositive ? MoveUpRight : MoveDownRight;

  const getLegendContent = () => {
    if (!showLegend) {
      return;
    }

    if (props.tooltipContent) {
      return {
        ...legendContent,
        content: LegendContent,
        wrapperStyle: { marginTop: "15px" },
      };
    }

    return {
      ...legendContent,
      content: (props) => (
        <HpLegendContent
          gainPercentage={gainPercentage}
          gain={gain}
          benchmarkPerc={benchmarkPerc}
          benchmark={benchmark}
          activeValue={activeValue}
          firstValue={firstValue}
          isTooltipActive={isTooltipActive}
          bmKey={bmKey}
          stratKey={stratKey}
          activePeriod={activePeriod}
          {...props}
        />
      ),
      wrapperStyle: { marginTop: "15px" },
    };
  };

  const onMouseEnter = () => setIsTooltipActive(true);
  // Clear hover state — useMemo auto-falls back to last data point + server return
  const onMouseLeave = () => {
    setIsTooltipActive(false);
    setHoverPayload(null);
  };

  const getTooltipContent = (data) => {
    const rows = data?.payload || [];
    // Only show a color dot when there are multiple lines to disambiguate
    const showDot = rows.length > 1;

    return (
      <ChartTooltip {...data} activePeriod={activePeriod}>
        {rows.map((p, i) => {
          const row = getGainLossDisplay(firstValue, p.value);
          return (
            <div
              key={i}
              className={classNames("label", {
                gain: row.isGain,
                loss: !row.isGain,
              })}
            >
              {showDot && <TooltipColorDot $color={p.color} />}
              <span>{p.payload?.label || p.name} </span>
              <span>
                {row.sign}${row.formattedGain} ({row.sign}
                {row.formattedGainPercentage}%)
              </span>
            </div>
          );
        })}
      </ChartTooltip>
    );
  };

  if (isLoading) {
    return <HistoricalPerformanceChartPlaceholder {...props} />;
  }

  return (
    <HistoricalPerformanceChartContainer>
      {dataLen > 0 && (
        <>
          {/* Chart Header: show timeframe filter always; hide balance/profit if custom tooltip */}
          <div className="investment-value">
            <div className="total">
              <HeaderComponent>
                <div className="header-left">
                  {(!props.tooltipContent || alwaysShowHeaderBalance) && (
                    <>
                      <p className="current-balance">Current Balance</p>
                      <div className="amount">
                        $
                        {`${numberSeparator(activeValue)}`.includes(".") ? (
                          <>
                            {`${numberSeparator(activeValue)}`.split(".")[0]}
                            <span className="metric-value-decimal">
                              .{`${numberSeparator(activeValue)}`.split(".")[1]}
                            </span>
                          </>
                        ) : (
                          `${numberSeparator(activeValue)}`
                        )}
                      </div>
                      <div
                        className={classNames("extra-info", {
                          gain: isPositive,
                          loss: !isPositive,
                        })}
                      >
                        <div className="icon-wrapper">
                          <Icon size={20} strokeWidth={2.5} />
                        </div>

                        <span className="gain-text">
                          {isPositive ? "+" : "-"}${formattedGain} (
                          {formattedGainPerc}%)
                        </span>

                        <span className="period-text">{displayLabel}</span>
                      </div>
                    </>
                  )}
                </div>
                {timeframe && (
                  <Filter
                    timeframe={timeframe}
                    activePeriod={activePeriod}
                    handleFilterClick={handleFilterClick}
                    className="h-performance-filter"
                  />
                )}
              </HeaderComponent>
            </div>
          </div>

          {/* Chart */}
          <div
            style={{ width: "100%", height, position: "relative" }}
            className="chart-wrapper"
          >
            <LineChart
              data={data}
              lines={lines}
              lineChartProps={{ onMouseMove, onMouseEnter, onMouseLeave }}
              tooltipContent={props.tooltipContent || getTooltipContent}
              showAreaFill={true}
              xAxisProps={false}
              yAxisProps={{
                domain: calculateYAxisDomain(data),
                hide: true,
              }}
              {...props}
              legendContent={getLegendContent()}
            />
          </div>
        </>
      )}

      {/* No-Data state: keep original header layout with timeframe filter visible */}
      {!dataLen && (
        <>
          <div className="investment-value">
            <div className="total">
              <HeaderComponent>
                <div />
                {timeframe && (
                  <Filter
                    timeframe={timeframe}
                    activePeriod={activePeriod}
                    handleFilterClick={handleFilterClick}
                    className="h-performance-filter"
                  />
                )}
              </HeaderComponent>
            </div>
          </div>
          <div style={{ width: "100%", height }} className="chart-wrapper">
            {(() => {
              const isTimeframeSpecificNoData =
                Array.isArray(timeframe) &&
                timeframe.length > 0 &&
                !!activePeriod;
              const noDataText = isTimeframeSpecificNoData
                ? "No Data for selected timeframe"
                : nodata;
              return (
                <NoDataMessage className="no-data">{noDataText}</NoDataMessage>
              );
            })()}
          </div>
        </>
      )}
    </HistoricalPerformanceChartContainer>
  );
};

HistoricalPerformanceChartV2.propTypes = {
  height: PropTypes.string,
  data: PropTypes.array,
  lines: PropTypes.array,
  nodata: PropTypes.string,
  isLoading: PropTypes.bool,
  timeframe: PropTypes.array,
  handleFilterClick: PropTypes.func,
  activePeriod: PropTypes.string,
  tooltipContent: PropTypes.any,
  legendContent: PropTypes.any,
  showLegend: PropTypes.bool,
  alwaysShowHeaderBalance: PropTypes.bool,
  periodReturns: PropTypes.object,
};
